import type { RecipeFamily } from "../../domain/types";
import { option, pantry, slot } from "./helpers";

export const curryRecipes: RecipeFamily[] = [
  {
    id: "cauliflower-chickpea-curry",
    nameTemplate: "{vegetable.name} & {protein.name} coconut curry",
    translations: {
      tr: {
        nameTemplate:
          "{vegetable.name} ve {protein.name} hindistancevizi körisi",
      },
    },
    foodType: "curry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 35,
    slots: [
      slot("vegetable", "Main vegetable", true, "cauliflower", [
        option("cauliflower", 400, "g", "cut into small florets", 3),
        option("broccoli", 400, "g", "cut into small florets", 2),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("black-beans", 240, "g", "drained", 1),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("tomato", "Sauce base", true, "tomato", [
        option("tomato", 300, "g", "crushed"),
      ]),
      slot("cream", "Creamy element", true, "coconut-milk", [
        option("coconut-milk", 300, "ml"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 2, "tsp", undefined, 3),
        option("smoked-paprika", 2, "tsp", undefined, 2),
      ]),
      slot("finish", "Fresh finish", false, "cilantro", [
        option("cilantro", 15, "g", "chopped", 3),
        option("lemon", 1, "piece", "cut into wedges", 2),
      ]),
      pantry.salt,
    ],
    steps: [
      {
        id: "soften-aromatics",
        template:
          "Soften {onion.name} in a wide pot for 5 minutes. Add {garlic.name} and {spice.name}; stir for 30 seconds.",
        translations: {
          tr: {
            template:
              "Geniş bir tencerede {onion.name} 5 dakika yumuşatın. {garlic.name} ve {spice.name} ekleyin; 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "build-sauce",
        template:
          "Add {tomato.name}, {cream.name}, {vegetable.name}, and {salt.name}. Bring to a gentle simmer.",
        translations: {
          tr: {
            template:
              "{tomato.name}, {cream.name}, {vegetable.name} ve {salt.name} ekleyin. Hafifçe kaynamaya bırakın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Cover loosely and cook for 15 minutes. Add {protein.name} and simmer for 5 minutes more, until the vegetable is tender.",
        translations: {
          tr: {
            template:
              "Gevşekçe örtün ve 15 dakika pişirin. {protein.name} ekleyin ve sebze yumuşayana kadar 5 dakika daha kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Divide {base.name} between bowls, spoon over the curry, and add {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} kaselere paylaştırın, üzerine köriyi kaşıklayın ve kullanıyorsanız {finish.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["bulk", "protein", "starch", "fat", "aroma"],
    editorialPriority: 10,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "tofu-spinach-curry",
    nameTemplate: "Ginger {protein.name} & {greens.name} curry",
    translations: {
      tr: { nameTemplate: "Zencefilli {protein.name} ve {greens.name} körisi" },
    },
    foodType: "curry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 14,
    totalMinutes: 30,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("greens", "Greens", true, "spinach", [
        option("spinach", 200, "g", "roughly chopped", 3),
        option("broccoli", 300, "g", "cut into small florets", 1),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("cream", "Sauce", true, "coconut-milk", [
        option("coconut-milk", 300, "ml"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely grated"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("spice", "Spice", true, "cumin", [option("cumin", 2, "tsp")]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      pantry.water(150),
      pantry.salt,
    ],
    steps: [
      {
        id: "brown-protein",
        template:
          "Warm {oil.name} in a deep pan. Brown {protein.name} for 5 minutes, then move it to a plate.",
        translations: {
          tr: {
            template:
              "Derin bir tavada {oil.name} ısıtın. {protein.name} 5 dakika kızartın, ardından bir tabağa alın.",
          },
        },
      },
      {
        id: "start-sauce",
        template:
          "Add {ginger.name}, {garlic.name}, and {spice.name}. Stir briefly, then add {cream.name}, {water.name}, and {salt.name}.",
        translations: {
          tr: {
            template:
              "{ginger.name}, {garlic.name} ve {spice.name} ekleyin. Kısaca karıştırın, ardından {cream.name}, {water.name} ve {salt.name} ekleyin.",
          },
        },
      },
      {
        id: "finish-curry",
        template:
          "Return {protein.name}, add {greens.name}, and simmer until the greens are tender and the sauce coats the protein.",
        translations: {
          tr: {
            template:
              "{protein.name} geri ekleyin, {greens.name} ilave edin ve yeşillikler yumuşayana ve sos proteine yapışana kadar kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "serve",
        template: "Add {finish.name} if using and serve over {base.name}.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ekleyin ve {base.name} üzerinde servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "fat", "bitter", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "lentil-sweet-potato-curry",
    nameTemplate: "{protein.name} & {vegetable.name} tomato curry",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {vegetable.name} domatesli körisi",
      },
    },
    foodType: "curry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 40,
    slots: [
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 180, "g", "rinsed", 3),
        option("chickpeas", 240, "g", "drained", 1),
      ]),
      slot("vegetable", "Hearty vegetable", true, "sweet-potato", [
        option("sweet-potato", 350, "g", "cut into 2 cm cubes", 3),
        option("potato", 350, "g", "cut into 2 cm cubes", 2),
      ]),
      slot("tomato", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely grated"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 2, "tsp", undefined, 3),
        option("smoked-paprika", 2, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("lime", 1, "piece", "juiced", 2),
        option("cilantro", 15, "g", "chopped", 1),
      ]),
      pantry.water(500),
      pantry.salt,
    ],
    steps: [
      {
        id: "soften-base",
        template:
          "Warm {oil.name} in a pot. Soften {onion.name} for 5 minutes, then stir in {ginger.name} and {spice.name}.",
        translations: {
          tr: {
            template:
              "Bir tencerede {oil.name} ısıtın. {onion.name} 5 dakika yumuşatın, ardından {ginger.name} ve {spice.name} karıştırın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {vegetable.name}, {protein.name}, {tomato.name}, {water.name}, and {salt.name}. Simmer gently for 25 minutes.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {protein.name}, {tomato.name}, {water.name} ve {salt.name} ekleyin. Kısık ateşte 25 dakika pişirin.",
          },
        },
      },
      {
        id: "check",
        template:
          "Cook until the protein and vegetables are tender and the sauce is thick. Add a splash of water if needed.",
        translations: {
          tr: {
            template:
              "Protein ve sebzeler yumuşayana ve sos koyulaşana kadar pişirin. Gerekirse bir miktar su ekleyin.",
          },
        },
      },
      {
        id: "finish",
        template: "Add {finish.name} if using, taste, and serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ekleyin, tadına bakın ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "sweet", "acid", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "baked-chickpea-curry",
    nameTemplate: "Baked {protein.name} & {vegetable.name} curry",
    translations: {
      tr: { nameTemplate: "Fırında {protein.name} ve {vegetable.name} körisi" },
    },
    foodType: "curry",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 45,
    slots: [
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 400, "g", "drained", 3),
        option("lentils", 240, "g", "cooked and drained", 2),
      ]),
      slot("sauce", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed", 3),
        option("coconut-milk", 400, "ml", undefined, 2),
      ]),
      slot("vegetable", "Vegetable", true, "cauliflower", [
        option("cauliflower", 350, "g", "cut into florets", 3),
        option("sweet-potato", 350, "g", "cut into 2 cm cubes", 2),
        option("eggplant", 300, "g", "cut into 2 cm cubes", 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 2, "tsp", undefined, 3),
        option("smoked-paprika", 2, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("green", "Leafy green", false, "spinach", [
        option("spinach", 80, "g", "roughly chopped", 3),
        option("cilantro", 15, "g", "chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 190°C.",
        translations: { tr: { template: "Fırını 190°C'ye ısıtın." } },
      },
      {
        id: "combine",
        template:
          "In a deep baking dish, combine {sauce.name}, {protein.name}, {vegetable.name}, {garlic.name}, {ginger.name}, {spice.name}, {oil.name}, {salt.name}, and {black-pepper.name}. Stir well.",
        translations: {
          tr: {
            template:
              "Derin bir fırın kabında {sauce.name}, {protein.name}, {vegetable.name}, {garlic.name}, {ginger.name}, {spice.name}, {oil.name}, {salt.name} ve {black-pepper.name} karıştırın. İyice karıştırın.",
          },
        },
      },
      {
        id: "bake",
        template:
          "Cover tightly with foil and bake for 30 to 35 minutes, until the vegetable is tender and the sauce is bubbling.",
        translations: {
          tr: {
            template:
              "Folyoyla sıkıca örtün ve sebze yumuşayana ve sos kaynamaya başlayana kadar 30-35 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {green.name} if using and let it wilt for 2 minutes. Serve with rice or bread.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {green.name} karıştırın ve 2 dakika solmasına izin verin. Pirinç veya ekmekle servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "acid", "aroma", "heat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "tofu-tomato-onion-curry",
    nameTemplate: "{protein.name} & {vegetable.name} tomato curry",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {vegetable.name} domatesli körisi",
      },
    },
    foodType: "curry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 16,
    totalMinutes: 35,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("vegetable", "Main vegetable", true, "cauliflower", [
        option("cauliflower", 350, "g", "cut into small florets", 3),
        option("eggplant", 300, "g", "cut into 2 cm cubes", 2),
        option("bell-pepper", 300, "g", "cut into chunks", 1),
      ]),
      slot("tomato", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 2, "tsp", undefined, 3),
        option("smoked-paprika", 2, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("finish", "Fresh finish", false, "cilantro", [
        option("cilantro", 15, "g", "chopped", 3),
        option("lemon", 1, "piece", "cut into wedges", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "brown-tofu",
        template:
          "Warm {oil.name} in a wide pan over medium-high heat. Add {protein.name} and cook for 5 minutes, turning once, until golden. Move to a plate.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} orta-yüksek ateşte ısıtın. {protein.name} ekleyin ve bir kez çevirerek 5 dakika altın rengi olana kadar pişirin. Bir tabağa alın.",
          },
        },
      },
      {
        id: "soften-aromatics",
        template:
          "In the same pan, cook {onion.name} for 5 minutes until soft. Stir in {garlic.name}, {ginger.name}, and {spice.name} for 30 seconds.",
        translations: {
          tr: {
            template:
              "Aynı tavada {onion.name} 5 dakika yumuşayana kadar pişirin. {garlic.name}, {ginger.name} ve {spice.name} 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "build-sauce",
        template:
          "Add {tomato.name}, {vegetable.name}, {salt.name}, and {black-pepper.name}. Simmer for 12 minutes until the vegetable is tender.",
        translations: {
          tr: {
            template:
              "{tomato.name}, {vegetable.name}, {salt.name} ve {black-pepper.name} ekleyin. Sebze yumuşayana kadar 12 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Return {protein.name} to the pan and heat through for 3 minutes. Add {finish.name} if using and serve over {base.name}.",
        translations: {
          tr: {
            template:
              "{protein.name} tavaya geri ekleyin ve 3 dakika ısıtın. Kullanıyorsanız {finish.name} ekleyin ve {base.name} üzerinde servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "acid", "umami", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "baked-tofu-tomato-onion-curry",
    nameTemplate: "Baked {protein.name} & {vegetable.name} tomato curry",
    translations: {
      tr: {
        nameTemplate:
          "Fırında {protein.name} ve {vegetable.name} domatesli körisi",
      },
    },
    foodType: "curry",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 46,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("chickpeas", 400, "g", "drained", 2),
      ]),
      slot("sauce", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced"),
      ]),
      slot("vegetable", "Vegetable", true, "eggplant", [
        option("eggplant", 300, "g", "cut into 2 cm cubes", 3),
        option("cauliflower", 350, "g", "cut into small florets", 2),
        option("bell-pepper", 300, "g", "cut into chunks", 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 2, "tsp", undefined, 3),
        option("smoked-paprika", 2, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3),
        option("parsley", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 200°C.",
        translations: { tr: { template: "Fırını 200°C'ye ısıtın." } },
      },
      {
        id: "combine",
        template:
          "In a large baking dish, stir together {sauce.name}, {onion.name}, {garlic.name}, {ginger.name}, {vegetable.name}, {spice.name}, {oil.name}, {salt.name}, and {black-pepper.name}. Nestle {protein.name} into the sauce.",
        translations: {
          tr: {
            template:
              "Büyük bir fırın kabında {sauce.name}, {onion.name}, {garlic.name}, {ginger.name}, {vegetable.name}, {spice.name}, {oil.name}, {salt.name} ve {black-pepper.name} karıştırın. {protein.name} sosin içine yerleştirin.",
          },
        },
      },
      {
        id: "bake",
        template:
          "Cover tightly with foil and bake for 30 minutes. Uncover and bake for a further 4 minutes until the {protein.name} is lightly golden at the edges.",
        translations: {
          tr: {
            template:
              "Folyoyla sıkıca örtün ve 30 dakika pişirin. Örtüyü kaldırın ve {protein.name} kenarları hafif altın rengi olana kadar 4 dakika daha pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Scatter {herb.name} if using and serve with rice or flatbread.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {herb.name} serpin ve pirinç veya yassı ekmekle servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "acid", "aroma", "fat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "eggplant-lentil-oven-curry",
    nameTemplate: "Oven-baked {vegetable.name} & {protein.name} curry",
    translations: {
      tr: { nameTemplate: "Fırında {vegetable.name} ve {protein.name} körisi" },
    },
    foodType: "curry",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 14,
    totalMinutes: 44,
    slots: [
      slot("vegetable", "Main vegetable", true, "eggplant", [
        option("eggplant", 400, "g", "cut into 2 cm cubes", 3),
        option("sweet-potato", 400, "g", "cut into 2 cm cubes", 2),
        option("cauliflower", 400, "g", "cut into small florets", 1),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 260, "g", "cooked and drained", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("sauce", "Sauce", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("spice", "Spice blend", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3),
        option("parsley", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 200°C.",
        translations: { tr: { template: "Fırını 200°C'ye ısıtın." } },
      },
      {
        id: "coat-and-roast",
        template:
          "Toss {vegetable.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name} in a large baking dish. Roast for 15 minutes.",
        translations: {
          tr: {
            template:
              "Büyük bir fırın kabında {vegetable.name} ile {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} karıştırın. 15 dakika kavurun.",
          },
        },
      },
      {
        id: "add-sauce",
        template:
          "Stir in {protein.name}, {sauce.name}, {garlic.name}, and {ginger.name}.",
        translations: {
          tr: {
            template:
              "{protein.name}, {sauce.name}, {garlic.name} ve {ginger.name} ekleyip karıştırın.",
          },
        },
      },
      {
        id: "finish-bake",
        template:
          "Return to the oven for 20 minutes, until the {vegetable.name} is tender and the sauce has thickened. Scatter {herb.name} if using and serve.",
        translations: {
          tr: {
            template:
              "{vegetable.name} yumuşayana ve sos koyulaşana kadar fırına geri koyun ve 20 dakika pişirin. Kullanıyorsanız {herb.name} serpin ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "acid", "aroma", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "peanut-sweet-potato-curry",
    nameTemplate: "Peanut {vegetable.name} & {protein.name} curry",
    translations: {
      tr: {
        nameTemplate: "Fıstıklı {vegetable.name} ve {protein.name} körisi",
      },
    },
    foodType: "curry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 35,
    slots: [
      slot("vegetable", "Main vegetable", true, "sweet-potato", [
        option("sweet-potato", 400, "g", "cut into 2 cm cubes", 3),
        option("carrot", 350, "g", "cut into rounds", 2),
        option("potato", 400, "g", "cut into 2 cm cubes", 1),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("tofu", 280, "g", "pressed and cubed", 2),
        option("black-beans", 240, "g", "drained", 1),
      ]),
      slot("sauce", "Peanut sauce", true, "peanut-butter", [
        option("peanut-butter", 3, "tbsp"),
      ]),
      slot("liquid", "Liquid base", true, "coconut-milk", [
        option("coconut-milk", 200, "ml", undefined, 3),
        option("tomato", 300, "g", "crushed", 1),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced"),
      ]),
      slot("aromatic", "Garlic, ginger & spice", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped", 4),
        option("ginger", 15, "g", "finely grated", 3),
        option("cumin", 1, "tsp", undefined, 2),
        option("smoked-paprika", 1, "tsp", undefined, 1),
      ]),
      slot("acid", "Bright acid", true, "lime", [
        option("lime", 1, "piece", "juiced", 3),
        option("lemon", 1, "piece", "juiced", 1),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("finish", "Finish", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3),
        option("scallion", 2, "piece", "sliced", 1),
      ]),
      pantry.water(200),
      pantry.salt,
    ],
    steps: [
      {
        id: "soften-aromatics",
        template:
          "Warm {oil.name} in a pot over medium heat. Cook {onion.name} for 5 minutes until soft. Add {aromatic.name}; stir for 30 seconds.",
        translations: {
          tr: {
            template:
              "Bir tencerede {oil.name} orta ateşte ısıtın. {onion.name} 5 dakika yumuşayana kadar pişirin. {aromatic.name} ekleyin; 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "build-sauce",
        template:
          "Whisk in {sauce.name} and {liquid.name} until smooth. Add {vegetable.name}, {water.name}, and {salt.name}.",
        translations: {
          tr: {
            template:
              "{sauce.name} ve {liquid.name} pürüzsüz olana kadar çırpın. {vegetable.name}, {water.name} ve {salt.name} ekleyin.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Bring to a gentle simmer, cover, and cook for 15 minutes until the {vegetable.name} is tender.",
        translations: {
          tr: {
            template:
              "Hafifçe kaynamaya getirin, kapağını kapatın ve {vegetable.name} yumuşayana kadar 15 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {protein.name} and {acid.name} juice. Heat for 3 minutes. Serve over {base.name} and top with {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {acid.name} suyunu karıştırın. 3 dakika ısıtın. {base.name} üzerine servis yapın ve kullanıyorsanız {finish.name} ile tamamlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "fat", "sweet", "acid"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "green-bean-chickpea-coconut-curry",
    nameTemplate: "{vegetable.name} & {protein.name} coconut curry",
    translations: {
      tr: {
        nameTemplate:
          "{vegetable.name} ve {protein.name} hindistancevizi körisi",
      },
    },
    foodType: "curry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 14,
    totalMinutes: 28,
    slots: [
      slot("vegetable", "Main vegetable", true, "green-beans", [
        option("green-beans", 350, "g", "trimmed and halved", 3),
        option("zucchini", 350, "g", "cut into half-moons", 2),
        option("broccoli", 350, "g", "cut into florets", 1),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("tofu", 280, "g", "pressed and cubed", 2),
        option("white-beans", 240, "g", "drained", 1),
      ]),
      slot("cream", "Coconut cream", true, "coconut-milk", [
        option("coconut-milk", 400, "ml"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1.5, "tsp", undefined, 3),
        option("smoked-paprika", 1.5, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("noodles", 300, "g", "cooked and drained", 1),
      ]),
      slot("acid", "Bright finish", true, "lime", [
        option("lime", 1, "piece", "juiced", 3),
        option("lemon", 1, "piece", "juiced", 1),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3),
        option("basil", 15, "g", "torn", 2),
      ]),
      pantry.salt,
    ],
    steps: [
      {
        id: "start-aromatics",
        template:
          "Warm {oil.name} in a wide pan over medium heat. Cook {garlic.name}, {ginger.name}, and {spice.name} for 1 minute until fragrant.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} orta ateşte ısıtın. {garlic.name}, {ginger.name} ve {spice.name} koku salana kadar 1 dakika pişirin.",
          },
        },
      },
      {
        id: "build-curry",
        template:
          "Pour in {cream.name} and bring to a gentle simmer. Add {vegetable.name} and {salt.name}.",
        translations: {
          tr: {
            template:
              "{cream.name} dökün ve hafifçe kaynamaya getirin. {vegetable.name} ve {salt.name} ekleyin.",
          },
        },
      },
      {
        id: "cook",
        template:
          "Cover and cook for 8 minutes until the {vegetable.name} is just tender.",
        translations: {
          tr: {
            template:
              "Kapağını kapatın ve {vegetable.name} sadece yumuşayana kadar 8 dakika pişirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir in {protein.name} and {acid.name} juice; heat for 2 minutes. Serve over {base.name} with {herb.name} if using.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {acid.name} suyunu karıştırın; 2 dakika ısıtın. {base.name} üzerinde servis yapın ve kullanıyorsanız {herb.name} ile tamamlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "fat", "acid", "fresh"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "spicy-braised-tofu",
    nameTemplate: "Spicy braised {protein.name} with {heat.name}",
    translations: {
      tr: { nameTemplate: "{heat.name} ile baharatlı {protein.name} yahnisi" },
    },
    foodType: "curry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 400, "g", "soft or firm, cut into 2 cm cubes", 3),
        option("tempeh", 250, "g", "crumbled or thinly sliced", 2),
        option("edamame", 200, "g", "shelled", 1),
      ]),
      slot("heat", "Chili sauce", true, "chili-garlic-sauce", [
        option("chili-garlic-sauce", 2, "tbsp", undefined, 3),
        option("chili-flakes", 1, "tsp", undefined, 2),
      ]),
      slot("soy", "Soy sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely minced"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("scallion", "Scallion", true, "scallion", [
        option(
          "scallion",
          3,
          "piece",
          "thinly sliced, whites and greens separated",
        ),
      ]),
      slot("grain", "Grain base", true, "rice", [
        option("rice", 180, "g", "cooked", 3),
        option("spaghetti", 140, "g", "cooked", 2),
      ]),
      slot("oil", "Cooking fat", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp"),
      ]),
      pantry.salt,
      pantry.water(150),
    ],
    steps: [
      {
        id: "drain-tofu",
        template:
          "Gently press {protein.name} between paper towels for 5 minutes to remove excess moisture. Cut into 2 cm cubes if not already done.",
        translations: {
          tr: {
            template:
              "{protein.name} fazla nemini almak için kağıt havlu arasında 5 dakika nazikçe sıkıştırın. Henüz yapılmadıysa 2 cm küpler halinde kesin.",
          },
        },
      },
      {
        id: "build-sauce",
        template:
          "Stir together {heat.name}, {soy.name}, and {water.name} in a small bowl.",
        translations: {
          tr: {
            template:
              "Küçük bir kasede {heat.name}, {soy.name} ve {water.name} karıştırın.",
          },
        },
      },
      {
        id: "aromatics",
        template:
          "Heat {oil.name} in a non-stick wok or wide heavy pan over medium heat. Add scallion whites, {garlic.name}, and {ginger.name}. Stir for 1 minute until fragrant — do not let them brown.",
        translations: {
          tr: {
            template:
              "Yapışmaz bir wok veya geniş ağır tavada {oil.name} orta ateşte ısıtın. Yeşil soğanın beyaz kısmını, {garlic.name} ve {ginger.name} ekleyin. Koku salana kadar 1 dakika karıştırın — kızarmasına izin vermeyin.",
          },
        },
      },
      {
        id: "braise",
        template:
          "Pour the sauce into the pan and bring to a gentle simmer. Carefully slide {protein.name} into the sauce in a single layer. Spoon sauce over the top and braise gently for 5–6 minutes, turning once, until the tofu is heated through and the sauce has thickened slightly.",
        translations: {
          tr: {
            template:
              "Sosu tavaya dökün ve hafifçe kaynamaya getirin. {protein.name} dikkatlice tek sıra halinde sosa kaydırın. Üzerine sos kaşıklayın ve bir kez çevirerek 5-6 dakika nazikçe yahni yapın; tofunun ısınması ve sosun hafifçe koyulaşması gerekir.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Spoon {protein.name} and all the sauce over {grain.name}. Scatter scallion greens over the top.",
        translations: {
          tr: {
            template:
              "{protein.name} ve tüm sosu {grain.name} üzerine kaşıklayın. Üzerine yeşil soğanın yeşil kısmını serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "heat", "umami", "salt", "starch"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "adobo-braise",
    nameTemplate: "Adobo-braised {protein.name} with {grain.name}",
    translations: {
      tr: {
        nameTemplate: "Adobo usulü {protein.name} yahnisi ve {grain.name}",
      },
    },
    foodType: "curry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 35,
    slots: [
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 400, "g", "drained and rinsed", 3),
        option("tofu", 400, "g", "firm, pressed and cut into 3 cm pieces", 2),
        option("tempeh", 250, "g", "cut into 2 cm slices", 1),
      ]),
      slot("vinegar", "Vinegar (dominant acid)", true, "rice-vinegar", [
        option("rice-vinegar", 80, "ml", undefined, 3),
        option("red-wine-vinegar", 60, "ml", undefined, 2),
      ]),
      slot("soy", "Soy sauce", true, "soy-sauce", [
        option("soy-sauce", 60, "ml"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 6, "piece", "lightly smashed"),
      ]),
      slot("grain", "Grain base", true, "rice", [
        option("rice", 180, "g", "cooked", 3),
        option("spaghetti", 140, "g", "cooked", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
        option("sesame-oil", 1, "tbsp"),
      ]),
      pantry.pepper,
      pantry.water(100),
    ],
    steps: [
      {
        id: "sear",
        template:
          "Heat {oil.name} in a wide heavy pan over medium-high heat. Add {protein.name} and cook, undisturbed, for 2–3 minutes until lightly browned.",
        translations: {
          tr: {
            template:
              "Geniş ağır bir tavada {oil.name} orta-yüksek ateşte ısıtın. {protein.name} ekleyin ve karıştırmadan hafif kahverengi olana kadar 2-3 dakika pişirin.",
          },
        },
      },
      {
        id: "braise",
        template:
          "Add {vinegar.name}, {soy.name}, {garlic.name}, {water.name}, and a generous amount of {black-pepper.name}. Stir to combine. Bring to a boil, then reduce heat and simmer uncovered for 20–25 minutes, turning the {protein.name} occasionally, until the liquid reduces by half and becomes a glossy glaze.",
        translations: {
          tr: {
            template:
              "{vinegar.name}, {soy.name}, {garlic.name}, {water.name} ve bol miktarda {black-pepper.name} ekleyin. Karıştırın. Kaynamaya getirin, ardından ateşi kısın ve sıvı yarıya inip parlak bir sırça haline gelene kadar {protein.name} ara ara çevirerek 20-25 dakika açık kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "reduce",
        template:
          "Once the sauce coats the back of a spoon, taste — it should be assertively sour-savoury. Add a splash of water if the sauce has reduced too far and become sticky.",
        translations: {
          tr: {
            template:
              "Sos bir kaşığın arkasını kaplar hale gelince tadına bakın — belirgin biçimde ekşi-tuzlu olmalıdır. Sos fazla çekip yapışkan hale geldiyse biraz su ekleyin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Spoon {protein.name} and all the braising glaze over {grain.name}. Serve immediately.",
        translations: {
          tr: {
            template:
              "{protein.name} ve tüm yahni sırçasını {grain.name} üzerine kaşıklayın. Hemen servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "acid", "umami", "salt", "starch"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
];
